/**
 * @fileoverview Live tool-input drafting status (AI diff speedup)
 *
 * When an agent writes a large tool argument (e.g. the canvas HTML for
 * `proposeCanvasPatch` or a template rewrite for `proposeRewrite`), the chat
 * text stops moving until the whole argument is buffered — which used to
 * read as a stall. `deriveToolInputStatus` inspects the streaming UIMessages
 * (from `useStreamingUIMessages`) for a tool part in `input-streaming` state
 * and produces a one-line status ("Drafting canvas update… — 3.1k
 * characters") for `ChatMessageList`'s `activityStatus` pill.
 *
 * Used by:
 * - src/components/agents/universal/UniversalChatPanel.tsx
 * - src/ui/composed/editor/template-ai-chat-sheet.tsx
 */

/**
 * Live status labels for tools whose ARGUMENT generation is the slow part.
 * Anything not listed only shows a status once its input has grown past a
 * size threshold, so quick tool calls don't flash a pill.
 */
const TOOL_INPUT_STREAMING_LABELS: Record<string, string> = {
  proposeCanvasPatch: "Drafting canvas update",
  proposeRewrite: "Drafting template rewrite",
  updateTemplateFields: "Updating template fields",
  logTimeEntries: "Logging time",
  proposeTaskCreate: "Drafting a task",
  proposeTaskPatch: "Drafting a task change",
  proposeEntityPatch: "Drafting a change",
  proposeAssetPatch: "Drafting an asset change",
  proposeContactPatch: "Drafting a contact change",
  createChart: "Building a chart",
  createSpreadsheet: "Building a spreadsheet",
};

/**
 * Derive a one-line live status from the streaming UIMessages: when the
 * model is mid-way through writing a tool call's input, surface what it's
 * doing plus how much it has written, so the user sees motion instead of a
 * stalled chat. Returns null when nothing is mid-flight.
 */
export function deriveToolInputStatus(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  streaming: any[] | undefined,
): string | null {
  const last = streaming?.[streaming.length - 1];
  if (!last || last.status !== "streaming") return null;
  for (let i = (last.parts?.length ?? 0) - 1; i >= 0; i--) {
    const part = last.parts[i];
    if (part?.state !== "input-streaming") continue;
    const toolName =
      part.type === "dynamic-tool"
        ? String(part.toolName ?? "")
        : typeof part.type === "string" && part.type.startsWith("tool-")
          ? part.type.slice("tool-".length)
          : "";
    if (!toolName) continue;
    const chars =
      typeof part.input === "string"
        ? part.input.length
        : JSON.stringify(part.input ?? "").length;
    const label = TOOL_INPUT_STREAMING_LABELS[toolName];
    if (!label && chars < 400) continue;
    const progress =
      chars >= 1000
        ? ` — ${(chars / 1000).toFixed(1)}k characters`
        : chars >= 400
          ? ` — ${chars} characters`
          : "";
    return `${label ?? "Working"}…${progress}`;
  }
  return null;
}
